'use client';
import { useEffect } from 'react';

 import Link from'next/link';
 import Header from'@/components/common/Header';
 import Footer from'@/components/common/Footer';
 import Icon from'@/components/ui/AppIcon';

const entryPoints = [
  {
    id: 'entry_evaluating',
    icon: 'MagnifyingGlassIcon',
    title: 'Evaluating S/4HANA',
    description: 'Understand capabilities, fit, and strategic implications',
    href: '/s-4-landscape-map',
  },
  {
    id: 'entry_planning',
    icon: 'MapIcon',
    title: 'Planning a Program',
    description: 'Migration strategies, timelines, and resource planning',
    href: '/migration-paths',
  },
  {
    id: 'entry_designing',
    icon: 'CubeIcon',
    title: 'Designing Solutions',
    description: 'Architecture patterns, integration, and extensibility',
    href: '/technical-playbook',
  },
  {
    id: 'entry_migrating',
    icon: 'ArrowPathIcon',
    title: 'Migrating from ECC',
    description: 'Greenfield, brownfield, and bluefield approaches',
    href: '/migration-paths',
  },
  {
    id: 'entry_operating',
    icon: 'CogIcon',
    title: 'Operating S/4',
    description: 'Deployment models, monitoring, and maintenance',
    href: '/technical-playbook#operations',
  },
  {
    id: 'entry_explaining',
    icon: 'ChatBubbleLeftRightIcon',
    title: 'Explaining to Executives',
    description: 'Business context, value drivers, and decision frameworks',
    href: '/homepage#executive-brief',
  },
]

const coreSections = [
  {
    id: 'section_landscape',
    title: 'S/4 Landscape Map',
    count: '120+ components',
    description: 'Architecture layers, modules, integration points',
    href: '/s-4-landscape-map',
  },
  {
    id: 'section_functional',
    title: 'Functional Playbook',
    count: '15 modules',
    description: 'Business processes, capabilities, module relationships',
    href: '/functional-playbook',
  },
  {
    id: 'section_technical',
    title: 'Technical Playbook',
    count: '8 domains',
    description: 'Technologies, patterns, deployment models',
    href: '/technical-playbook',
  },
  {
    id: 'section_migration',
    title: 'Migration Paths',
    count: '3 strategies',
    description: 'Greenfield, brownfield, bluefield comparisons',
    href: '/migration-paths',
  },
  {
    id: 'section_index',
    title: 'A-Z Index',
    count: '500+ terms',
    description: 'Searchable reference of all concepts',
    href: '/a-z-index',
  },
]

const principles = [
  {
    id: 'principle_neutral',
    title: 'Neutral',
    description: 'No vendor bias, no marketing language, no sales agenda. Just structure.',
  },
  {
    id: 'principle_complete',
    title: 'Complete',
    description: 'All modules, all layers, all integration points. The full landscape.',
  },
  {
    id: 'principle_structured',
    title: 'Structured',
    description: 'Organized for architects - relationships, dependencies, patterns.',
  },
  {
    id: 'principle_reference',
    title: 'Reference-Grade',
    description: 'Designed to stay open during real work. Maps, not manuals.',
  },
  {
    id: 'principle_open',
    title: 'Open',
    description: 'MIT licensed. Community contributions welcome.',
  },
]

export default function Homepage() {
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal')
    if (!reveals.length) return

    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    )
    reveals.forEach(el => observer.observe(el))

    return () => observer.disconnect()
  }, [])

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      <main className="pt-24">
        {/* Hero Section */}
        <section className="mx-auto max-w-7xl px-6 py-20 md:py-32">
          <div className="text-center space-y-8">
            <h1 className="font-serif text-display-lg md:text-display-xl font-bold tracking-tight reveal">
              S/4HANA PLAYBOOK
            </h1>
            <p className="font-mono text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto reveal">
              An architect-grade reference for SAP S/4HANA. Not marketing. Not training. Just maps.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4 reveal">
              <Link
                href="/s-4-landscape-map"
                className="px-6 py-3 bg-primary text-primary-foreground font-mono text-sm rounded hover:opacity-90 transition-opacity"
              >
                Explore Landscape
              </Link>
              <Link
                href="/a-z-index"
                className="px-6 py-3 border border-border font-mono text-sm rounded hover:bg-muted transition-colors"
              >
                Browse A-Z Index
              </Link>
            </div>
          </div>

          {/* ASCII Art Diagram (minimal) */}
          <div className="mt-16 p-8 bg-card border border-border rounded font-mono text-xs overflow-x-auto reveal">
            <pre className="text-muted-foreground">
{`┌─────────────────────────────────────────────────────────────┐
│                    PRESENTATION LAYER                       │
│         Fiori · UI5 · Mobile · Analytics                    │
├─────────────────────────────────────────────────────────────┤
│                   APPLICATION LAYER                         │
│   Finance · SCM · Manufacturing · HR · Sales                │
├─────────────────────────────────────────────────────────────┤
│                   INTEGRATION LAYER                         │
│    APIs · OData · RFC · IDocs · BTP                         │
├─────────────────────────────────────────────────────────────┤
│                      DATA LAYER                             │
│              SAP HANA · Persistence                         │
└─────────────────────────────────────────────────────────────┘`}
            </pre>
          </div>
        </section>

        {/* Entry Points Section */}
        <section className="mx-auto max-w-7xl px-6 py-20 border-t border-border">
          <div className="mb-12 reveal">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold mb-3">Entry Points</h2>
            <p className="text-muted-foreground font-mono text-sm">Start with your current context</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {entryPoints.map((entry) => (
              <Link
                key={entry.id}
                href={entry.href}
                className="p-6 border border-border rounded hover:border-primary transition-colors group reveal"
              >
                <Icon name={entry.icon as any} size={24} className="text-primary mb-4" variant="outline" />
                <h3 className="font-serif text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                  {entry.title}
                </h3>
                <p className="text-sm text-muted-foreground font-mono">{entry.description}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* Core Sections Overview */}
        <section className="mx-auto max-w-7xl px-6 py-20 border-t border-border">
          <div className="mb-12 reveal">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold mb-3">Core Sections</h2>
            <p className="text-muted-foreground font-mono text-sm">Complete coverage of S/4HANA landscape</p>
          </div>

          <div className="space-y-4">
            {coreSections.map((section) => (
              <Link
                key={section.id}
                href={section.href}
                className="block p-6 border border-border rounded hover:bg-muted transition-colors group reveal"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3 mb-2">
                      <h3 className="font-serif text-xl font-semibold group-hover:text-primary transition-colors">
                        {section.title}
                      </h3>
                      <span className="font-mono text-xs text-muted-foreground">{section.count}</span>
                    </div>
                    <p className="text-sm text-muted-foreground font-mono">{section.description}</p>
                  </div>
                  <Icon name="ChevronRightIcon" size={20} className="text-muted-foreground group-hover:text-primary transition-colors" variant="outline" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Design Philosophy */}
        <section className="mx-auto max-w-7xl px-6 py-20 border-t border-border">
          <div className="mb-12 reveal">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold mb-3">Design Philosophy</h2>
            <p className="text-muted-foreground font-mono text-sm">What makes this different</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {principles.slice(0, 3).map((principle) => (
              <div key={principle.id} className="reveal">
                <h3 className="font-serif text-xl font-semibold mb-3">{principle.title}</h3>
                <p className="text-sm text-muted-foreground font-mono leading-relaxed">{principle.description}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            {principles.slice(3).map((principle) => (
              <div key={principle.id} className="reveal">
                <h3 className="font-serif text-xl font-semibold mb-3">{principle.title}</h3>
                <p className="text-sm text-muted-foreground font-mono leading-relaxed">{principle.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Contact for Playbook Downloads */}
        <section className="mx-auto max-w-7xl px-6 py-20 border-t border-border">
          <div className="text-center space-y-6 reveal">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold mb-3">Download Playbooks</h2>
            <p className="text-muted-foreground font-mono text-sm max-w-2xl mx-auto">
              For downloadable versions of these playbooks or custom architecture guidance
            </p>
            <div className="pt-4">
              <a
                href="mailto:architects@s4playbook.com"
                className="inline-flex items-center gap-2 px-6 py-3 border border-border font-mono text-sm rounded hover:bg-muted transition-colors"
              >
                <Icon name="EnvelopeIcon" size={20} variant="outline" />
                architects@s4playbook.com
              </a>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="mx-auto max-w-7xl px-6 py-20 border-t border-border">
          <div className="text-center space-y-6 reveal">
            <p className="font-mono text-muted-foreground">
              Start with the Landscape Map or jump to any section
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="/s-4-landscape-map"
                className="px-6 py-3 bg-primary text-primary-foreground font-mono text-sm rounded hover:opacity-90 transition-opacity"
              >
                View Landscape
              </Link>
              <Link
                href="/a-z-index"
                className="px-6 py-3 border border-border font-mono text-sm rounded hover:bg-muted transition-colors"
              >
                Browse Index
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}